import React, { useEffect, useRef, useState } from "react";
import { Timestamp, collection, deleteDoc, doc, getDoc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { db, auth } from "../../firebase";
import { Link } from "react-router-dom";
import Window from "../../components/Window";
import Icon, { IconTile } from "../../components/Icon";
import GlitterBurst from "../../components/GlitterBurst";
import Envelope3D from "../../components/Envelope3D";
import {
  isUnreadLetter,
  isUnseenKudo,
  isWelcomeTrashed,
  markKudosSeen,
  markLetterRead,
  markWelcome,
} from "../../notifications";
import { INTRODUCE_LINK, TEAM_EMAIL, WELCOME_FROM, WELCOME_ID, WELCOME_SUBJECT, welcomeBody } from "../../welcome";
import { getMyAnonAdIds, perAd } from "../../penpal";
import { useBlocks } from "../../blocks";
import { SafetyActions } from "../../components/SafetyBits";
import ContentWarning from "../../components/ContentWarning";
import SlugAndWorm from "../../components/SlugAndWorm";
import "../../styles.css";

type Message = {
  id: string;
  subject: string;
  body: string;
  senderId: string;
  senderUsername?: string;
  receiverId?: string;
  receiverid?: string; // older messages used this spelling
  receiverUsername?: string;
  receiverAdId?: string; // sent to an anonymous ad
  timestamp?: any;
  read?: boolean;
  cw?: string; // optional content warning
  trashedByReceiver?: boolean; // each person can throw away their own copy
  trashedBySender?: boolean;
};

type Kudo = {
  id: string;
  from: string;
  fromUsername?: string;
  to?: string;
  toAd?: string;
  note?: string; // what it was for, e.g. 'for your post "vibe check"'
  timestamp: any;
  seen?: boolean;
};

const newestFirst = (a: { timestamp?: any }, b: { timestamp?: any }) =>
  (b.timestamp?.toMillis?.() ?? 0) - (a.timestamp?.toMillis?.() ?? 0);

const formatDate = (ts: any) =>
  ts?.toDate ? ts.toDate().toLocaleDateString() : "";

export default function Inbox() {
  const [user] = useAuthState(auth);
  const [allReceived, setReceivedMessages] = useState<Message[]>([]);
  const [sentMessages, setSentMessages] = useState<Message[]>([]);
  const [allKudos, setReceivedKudos] = useState<Kudo[]>([]);
  // Hide anything from people this user has blocked.
  const { ids: blocked } = useBlocks();
  const receivedMessages = allReceived.filter((m) => !blocked.has(m.senderId));
  const receivedKudos = allKudos.filter((k) => !blocked.has(k.from));
  const [loaded, setLoaded] = useState(false);
  const [mailboxOpen, setMailboxOpen] = useState(false);
  const [reading, setReading] = useState<{ msg: Message; sealed: boolean } | null>(
    null
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      // Messages are saved with "receiverId"; some older ones used "receiverid".
      const receivedQuery = query(
        collection(db, "messages"),
        where("receiverId", "==", user.uid)
      );
      const legacyReceivedQuery = query(
        collection(db, "messages"),
        where("receiverid", "==", user.uid)
      );
      const sentQuery = query(
        collection(db, "messages"),
        where("senderId", "==", user.uid)
      );
      const kudosQuery = query(
        collection(db, "kudos"),
        where("to", "==", user.uid)
      );

      // Plus anything addressed to this user's anonymous ads.
      const anonIds = await getMyAnonAdIds(user.uid).catch(() => [] as string[]);

      const [receivedSnap, legacySnap, sentSnap, kudosSnap, adLetterSnaps, adKudosSnaps] =
        await Promise.all([
          getDocs(receivedQuery),
          getDocs(legacyReceivedQuery),
          getDocs(sentQuery),
          getDocs(kudosQuery),
          Promise.all(
            perAd(anonIds, (adId) =>
              getDocs(query(collection(db, "messages"), where("receiverAdId", "==", adId)))
            )
          ),
          Promise.all(
            perAd(anonIds, (adId) =>
              getDocs(query(collection(db, "kudos"), where("toAd", "==", adId)))
            )
          ),
        ]);

      const received = new Map<string, Message>();
      [
        ...receivedSnap.docs,
        ...legacySnap.docs,
        ...adLetterSnaps.flatMap((s) => s.docs),
      ].forEach((doc) =>
        received.set(doc.id, { ...(doc.data() as Message), id: doc.id })
      );
      // Plus the team's welcome letter, until it's thrown away.
      const meSnap = await getDoc(doc(db, "users", user.uid)).catch(() => null);
      const me = meSnap?.data();
      if (me?.welcome !== "trashed" && !isWelcomeTrashed()) {
        const joined = user.metadata.creationTime ? new Date(user.metadata.creationTime) : new Date();
        received.set(WELCOME_ID, {
          id: WELCOME_ID,
          subject: WELCOME_SUBJECT,
          body: welcomeBody(me?.username),
          senderId: "",
          senderUsername: WELCOME_FROM,
          timestamp: Timestamp.fromDate(joined),
          read: !!me?.welcome,
        });
      }
      // Letters keep the name from when they were sent. If that was missing
      // ("unknown", e.g. before the sender chose a username), use their
      // current username instead.
      const names = new Map<string, Promise<string>>();
      const currentName = (uid: string) => {
        if (!names.has(uid)) {
          names.set(
            uid,
            getDoc(doc(db, "users", uid))
              .then((s) => (s.data()?.username as string) || "")
              .catch(() => "")
          );
        }
        return names.get(uid)!;
      };
      // ("anonymous" is never looked up, so anonymous posters stay anonymous.)
      const missing = (name?: string) => !name || name === "unknown";
      const withNames = (list: Message[], idKey: "senderId" | "receiverId", nameKey: "senderUsername" | "receiverUsername") =>
        Promise.all(
          list.map(async (m) => {
            const uid = m[idKey];
            if (!uid || !missing(m[nameKey])) return m;
            const name = await currentName(uid);
            return name ? { ...m, [nameKey]: name } : m;
          })
        );

      const receivedList = await withNames(
        Array.from(received.values()).filter((m) => !m.trashedByReceiver).sort(newestFirst),
        "senderId",
        "senderUsername"
      );
      setReceivedMessages(receivedList);
      setSentMessages(
        await withNames(
          sentSnap.docs
            .map((doc) => ({
              ...(doc.data() as Message),
              id: doc.id,
            }))
            .filter((m) => !m.trashedBySender)
            .sort(newestFirst),
          "receiverId",
          "receiverUsername"
        )
      );
      setReceivedKudos(
        await Promise.all(
          [...kudosSnap.docs, ...adKudosSnaps.flatMap((s) => s.docs)]
            .map((doc) => ({
              ...(doc.data() as Kudo),
              id: doc.id,
            }))
            .sort(newestFirst)
            .map(async (k) => {
              if (!k.from || !missing(k.fromUsername)) return k;
              const name = await currentName(k.from);
              return name ? { ...k, fromUsername: name } : k;
            })
        )
      );
      // With nothing new, skip the ceremony and show the letters.
      setMailboxOpen(!receivedList.some((m) => isUnreadLetter(m.id, m)));

      // Like a real mailbox, the flag goes up for outgoing mail: any letter
      // sent since the carrier last came by. (Never says whether it was read.)
      const lastPickup: number =
        meSnap?.data()?.mailPickedUpAt ?? Date.now() - 3 * 24 * 60 * 60 * 1000;
      const waiting = sentSnap.docs.filter(
        (d) => (d.data().timestamp?.toMillis?.() ?? 0) > lastPickup
      ).length;
      setOutgoing(waiting);
      setLoaded(true);
    };

    fetchData();
  }, [user]);

  // A moment after arriving, the carrier collects outgoing letters.
  const [outgoing, setOutgoing] = useState(0);
  const [pickup, setPickup] = useState<"none" | "waiting" | "collecting" | "done">("none");
  useEffect(() => {
    if (!user || outgoing === 0) return;
    setPickup("waiting");
    const t1 = window.setTimeout(() => {
      setPickup("collecting");
      updateDoc(doc(db, "users", user.uid), { mailPickedUpAt: Date.now() }).catch(
        (err) => console.warn("Couldn't record pickup:", err)
      );
    }, 1800);
    const t2 = window.setTimeout(() => setPickup("done"), 5200);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [user, outgoing]);

  const unreadCount = receivedMessages.filter((m) => isUnreadLetter(m.id, m)).length;

  const openLetter = (msg: Message) => {
    const unread = isUnreadLetter(msg.id, msg);
    setReading({ msg, sealed: unread });
    if (unread) {
      if (msg.id === WELCOME_ID) markWelcome(user!.uid, "read");
      else markLetterRead(msg.id);
      setReceivedMessages((prev) =>
        prev.map((m) => (m.id === msg.id ? { ...m, read: true } : m))
      );
    }
  };

  // Throw away your copy. The other person keeps theirs; once both have
  // thrown it away, it's deleted for good.
  const throwAway = async (msg: Message) => {
    const incoming = msg.senderId !== user?.uid;
    const ref = doc(db, "messages", msg.id);
    try {
      if (msg.id === WELCOME_ID) await markWelcome(user!.uid, "trashed");
      else if (incoming ? msg.trashedBySender : msg.trashedByReceiver) await deleteDoc(ref);
      else await updateDoc(ref, incoming ? { trashedByReceiver: true } : { trashedBySender: true });
    } catch (err) {
      console.error(err);
      alert("Sorry, that letter couldn't be thrown away. Please try again.");
      return;
    }
    if (incoming) setReceivedMessages((prev) => prev.filter((m) => m.id !== msg.id));
    else setSentMessages((prev) => prev.filter((m) => m.id !== msg.id));
    setReading(null);
  };

  return (
    <>
      <Window title="inbox.exe">
        <div className="flex items-center gap-4">
          <IconTile name="inbox" tone="blue" size={52} />
          <div>
            <h1 className="window-heading">Your Mailbox</h1>
            <p className="window-sub !mb-0">
              {!loaded
                ? "Walking out to the mailbox..."
                : unreadCount > 0
                ? `You've got mail! ${unreadCount} new letter${unreadCount === 1 ? "" : "s"} inside.`
                : receivedMessages.length > 0
                ? "All caught up. Click any letter to read it again."
                : "No letters yet, but they're on their way."}
            </p>
          </div>
        </div>
      </Window>

      <div className="grid items-start gap-5 lg:grid-cols-[250px_minmax(0,1fr)]">
        <div className="flex flex-col gap-5">
          <Window title="mailbox.exe" tone="tan">
            <div className="flex flex-col items-center gap-2 text-center">
              <Mailbox
                open={mailboxOpen}
                flagUp={pickup === "waiting"}
                collecting={pickup === "collecting"}
                hasNew={unreadCount > 0}
                letters={Math.min(receivedMessages.length, 3)}
                onClick={() => setMailboxOpen((o) => !o)}
              />
              {pickup === "waiting" && (
                <p className="text-sm font-semibold" role="status">
                  Flag's up: you have outgoing mail...
                </p>
              )}
              {pickup === "collecting" && (
                <p className="pickup-note" role="status">
                  ✧ the mail carrier picked up your letter{outgoing === 1 ? "" : "s"}! ✧
                </p>
              )}
              <p className="font-pixel text-[0.65rem]">
                {mailboxOpen ? (
                  "click to close"
                ) : (
                  <span className="blink-soft">click to open</span>
                )}
              </p>
            </div>
          </Window>

          <Window title={`kudos (${receivedKudos.length})`} tone="blue">
            {loaded && <KudosBox kudos={receivedKudos} />}
          </Window>
        </div>

        <div className="flex min-w-0 flex-col gap-5">
          <Window title={`received (${receivedMessages.length})`}>
            {!mailboxOpen ? (
              <p className="empty">
                <Icon name="lock" size={30} tone="ink" pop />
                <br />
                Your mailbox is closed. Open it to see your letters.
              </p>
            ) : receivedMessages.length === 0 ? (
              <p className="empty">
                <Icon name="mail" size={36} tone="pink" pop />
                <br />
                Your mailbox is empty for now.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {receivedMessages.map((msg, i) => {
                  const unread = isUnreadLetter(msg.id, msg);
                  return (
                    <li key={msg.id} className="letter-row-in" style={{ animationDelay: `${i * 60}ms` }}>
                      <button
                        type="button"
                        onClick={() => openLetter(msg)}
                        className={`letter-row${unread ? " letter-unread" : ""}`}
                      >
                        <Envelope3D open={false} width={46} float={false} tone={unread ? "pink" : "blue"} />
                        <span className="min-w-0 flex-1 text-left">
                          <span className="flex flex-wrap items-center gap-2 font-display text-lg font-semibold">
                            {msg.subject}
                            {unread && <span className="new-badge blink">NEW!</span>}
                          </span>
                          <span className="block text-sm text-ink-soft">
                            from @{msg.senderUsername || "someone"}
                            {msg.receiverAdId && " · to your anonymous ad"}
                            {msg.timestamp && ` · ${formatDate(msg.timestamp)}`}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Window>

          <Window title={`sent (${sentMessages.length})`} tone="tan">
            {sentMessages.length === 0 ? (
              <p className="empty">You haven't sent any letters yet.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {sentMessages.map((msg) => (
                  <li key={msg.id}>
                    <button
                      type="button"
                      onClick={() => setReading({ msg, sealed: false })}
                      className="letter-row"
                    >
                      <Icon name="send" size={22} tone="blue" pop />
                      <span className="min-w-0 flex-1 text-left">
                        <span className="block font-semibold">{msg.subject}</span>
                        <span className="block text-xs text-ink-soft">
                          {msg.receiverAdId
                            ? "to an anonymous writer"
                            : `to @${msg.receiverUsername || "someone"}`}
                          {msg.timestamp && ` · ${formatDate(msg.timestamp)}`}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Window>
        </div>
      </div>

      {reading && (
        <LetterViewer
          msg={reading.msg}
          sealed={reading.sealed}
          incoming={reading.msg.senderId !== user?.uid}
          onClose={() => setReading(null)}
          onThrowAway={() => throwAway(reading.msg)}
        />
      )}
    </>
  );
}

// A front-on mailbox. The door hinges at the bottom and swings toward you.
// Like a real one, the flag is raised for outgoing mail; when the carrier
// collects it, a letter flies off and the flag lowers.
function Mailbox({
  open,
  flagUp,
  collecting,
  hasNew,
  letters,
  onClick,
}: {
  open: boolean;
  flagUp: boolean;
  collecting: boolean;
  hasNew: boolean;
  letters: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={open ? "Close mailbox" : "Open mailbox"}
      aria-pressed={open}
      className={`mailbox${open ? " is-open" : ""}${flagUp ? " flag-up" : ""}${
        hasNew ? " has-new" : ""
      }`}
    >
      {collecting && <span className="mb-flyaway" aria-hidden="true" />}
      <span className="mb-post" />
      <span className="mb-flag">
        <span className="mb-flag-cloth" />
      </span>
      <span className="mb-body">
        <span className="mb-inside">
          {Array.from({ length: letters }).map((_, i) => (
            <span
              key={i}
              className="mb-letter"
              style={{ transform: `translateY(${-i * 7}px) rotate(${(i - 1) * 5}deg)` }}
            />
          ))}
        </span>
        <span className="mb-door">
          <span className="mb-handle" />
          <span className="mb-label">MAIL</span>
        </span>
      </span>
    </button>
  );
}

// Reading view. A sealed (unread) letter first pops out of its envelope.
function LetterViewer({
  msg,
  sealed,
  incoming,
  onClose,
  onThrowAway,
}: {
  msg: Message;
  sealed: boolean;
  incoming: boolean;
  onClose: () => void;
  onThrowAway: () => void;
}) {
  const [stage, setStage] = useState<"sealed" | "opening" | "read">(
    sealed ? "sealed" : "read"
  );
  const [burstFrom, setBurstFrom] = useState<{ x: number; y: number } | null>(null);
  const envRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (stage === "read") closeRef.current?.focus();
    else envRef.current?.focus();
  }, [stage]);

  const open = () => {
    const rect = envRef.current?.getBoundingClientRect();
    if (rect) setBurstFrom({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 3 });
    setStage("opening");
    window.setTimeout(() => setStage("read"), 1000);
  };

  const fromTeam = msg.id === WELCOME_ID; // the welcome letter: no replies or reports
  const replySubject = msg.subject?.startsWith("Re:") ? msg.subject : `Re: ${msg.subject}`;
  const who = incoming
    ? `from @${msg.senderUsername || "someone"}`
    : msg.receiverAdId
    ? "to an anonymous writer"
    : `to @${msg.receiverUsername || "someone"}`;
  // Writing back to someone who found you through an anonymous ad
  // reveals your username, so the compose page warns first.
  const replyLink = `/send?to=${msg.senderId}&subject=${encodeURIComponent(replySubject)}${
    msg.receiverAdId ? "&reveal=1" : ""
  }`;

  return (
    <div
      className="viewer-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-label={`Letter: ${msg.subject}`}
    >
      {burstFrom && (
        <GlitterBurst origin={burstFrom} onDone={() => setBurstFrom(null)} />
      )}

      {stage !== "read" ? (
        <div className="flex flex-col items-center gap-3 text-center">
          <button
            ref={envRef}
            type="button"
            className="env-button"
            onClick={open}
            disabled={stage === "opening"}
            aria-label="Open the envelope"
          >
            <Envelope3D open={stage === "opening"} width={260}>
              <Icon name="mail" size={28} tone="pink" pop />
              <span className="font-hand text-lg">{who}</span>
            </Envelope3D>
          </button>
          <p className="viewer-hint">
            {stage === "opening" ? "✧ unfolding... ✧" : "Click the envelope to open it"}
          </p>
        </div>
      ) : (
        <div className="viewer-letter">
          <Window title={`${msg.subject || "letter"}.txt`} tone="tan" minimizable={false}>
            <p className="mb-1 text-sm text-ink-soft">
              {who}
              {msg.timestamp && ` · ${formatDate(msg.timestamp)}`}
              {incoming && msg.receiverAdId && " · sent to your anonymous ad"}
            </p>
            <h2 className="window-heading mb-3">{msg.subject}</h2>
            <ContentWarning cw={msg.cw}>
              <div className="lined-paper max-h-[55vh] overflow-y-auto whitespace-pre-wrap font-hand !text-xl !leading-[1.8rem]">
                {msg.body}
                {fromTeam && (
                  <span className="mt-2 flex justify-end">
                    <SlugAndWorm scale={3} />
                  </span>
                )}
              </div>
            </ContentWarning>
            <div className="btn-row mt-4">
              {fromTeam ? (
                <>
                  <Link to={INTRODUCE_LINK} className="btn">
                    <Icon name="waving" size={16} /> Introduce yourself
                  </Link>
                  <Link to="/board" className="btn btn-plain">
                    <Icon name="pin" size={16} /> Visit the PenPal Board
                  </Link>
                  <a href={`mailto:${TEAM_EMAIL}`} className="btn btn-plain">
                    <Icon name="mail" size={16} /> Email us
                  </a>
                </>
              ) : (
                incoming && (
                  <Link to={replyLink} className="btn">
                    <Icon name="reply" size={16} /> Write back
                  </Link>
                )
              )}
              <button ref={closeRef} type="button" onClick={onClose} className="btn btn-plain">
                Fold it back up
              </button>
              <button
                type="button"
                className="btn btn-plain ml-auto"
                onClick={() =>
                  window.confirm(
                    "Throw this letter away? It'll be gone from your mailbox for good. This only throws away your copy."
                  ) && onThrowAway()
                }
              >
                <Icon name="trash" size={16} /> Throw away
              </button>
            </div>
            {incoming && !fromTeam && (
              <div className="mt-3">
                <SafetyActions
                  target={{
                    userId: msg.senderId,
                    username: msg.senderUsername,
                    messageId: msg.id,
                  }}
                />
              </div>
            )}
          </Window>
        </div>
      )}
    </div>
  );
}

// New kudos wait in a sealed envelope. Opening it tips the flap back in 3D
// and bursts glitter out of it, then lists who sent them.
function KudosBox({ kudos }: { kudos: Kudo[] }) {
  const [unseenIds] = useState(() =>
    kudos.filter((k) => isUnseenKudo(k.id, k)).map((k) => k.id)
  );
  const [stage, setStage] = useState<"sealed" | "opening" | "open">(
    unseenIds.length > 0 ? "sealed" : "open"
  );
  const [burstFrom, setBurstFrom] = useState<{ x: number; y: number } | null>(null);
  const envRef = useRef<HTMLButtonElement>(null);

  const open = () => {
    const rect = envRef.current?.getBoundingClientRect();
    if (rect) setBurstFrom({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 3 });
    setStage("opening");
    markKudosSeen(unseenIds);
    window.setTimeout(() => setStage("open"), 1500);
  };

  return (
    <>
      {burstFrom && (
        <GlitterBurst origin={burstFrom} onDone={() => setBurstFrom(null)} />
      )}

      {stage !== "open" ? (
        <div className="flex flex-col items-center gap-2 py-2 text-center">
          <button
            ref={envRef}
            type="button"
            onClick={open}
            disabled={stage === "opening"}
            className="env-button"
            aria-label={`Open ${unseenIds.length} new kudos`}
          >
            <Envelope3D open={stage === "opening"} width={170} tone="blue">
              <Icon name="gift" size={22} tone="pink" pop />
              <span className="font-display text-lg font-bold text-tpink-deep">
                +{unseenIds.length}
              </span>
            </Envelope3D>
          </button>
          <p className="font-display text-lg font-semibold">
            {stage === "opening" ? "✧ yay! ✧" : `${unseenIds.length} new kudos!`}
          </p>
          {stage === "sealed" && (
            <p className="blink-soft font-pixel text-[0.65rem]">click to open</p>
          )}
        </div>
      ) : kudos.length === 0 ? (
        <p className="empty !p-2">No kudos yet, but you're still amazing. ✿</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {kudos.map((kudo) => (
            <li
              key={kudo.id}
              className={`kudo-item${unseenIds.includes(kudo.id) ? " kudo-fresh" : ""}`}
            >
              <IconTile name="gift" tone="gold" size={30} />
              <span className="min-w-0 text-sm">
                <Link to={`/profile/${kudo.from}`}>
                  @{kudo.fromUsername || "someone"}
                </Link>{" "}
                sent you kudos{kudo.toAd && " on your anonymous ad"}
                {kudo.note && ` ${kudo.note}`}
                <span className="block text-xs text-ink-soft">
                  {formatDate(kudo.timestamp)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
